import { fetchExperienceList, fetchTransferOptions } from 'actions/ExperienceActions'
import { EXP_CATEGORY_AIRPORT_TRANSFER_ID, EXP_CATEGORY_AIRPORT_LOUNGE_ID } from 'constants/experience'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getComplimentaryTransferParams } from 'lib/experiences/experienceUtils'
import { EmptyArray } from 'lib/array/arrayUtils'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import { useEffect, useMemo } from 'react'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import getIsTasteOfGoldEligibleOrder from 'checkout/selectors/loyalty/getIsTasteOfGoldEligibleOrder'
import { isOrderInternationalTrip } from 'checkout/selectors/loyalty/getOrderIsLoyaltyInternationalTrip'
import determineOrderLoyaltyRedeemTransferEligibility from 'checkout/selectors/loyalty/determineOrderLoyaltyRedeemTransferEligibility'
import determineOrderLoyaltyTransferExperience from 'checkout/selectors/loyalty/determineOrderLoyaltyTransferExperience'

interface Options {
  order: App.Order
}

/**
 * Fetches the loyalty transfer experience + one-click transfer options for `order`.
 * Driven entirely by the passed order (not the route-viewed order), so it works on both the
 * order details page and the upcoming-escapes benefits card.
 */
function useLoyaltyOrderViewTransferData({ order }: Options) {
  const dispatch = useAppDispatch()
  useLoyaltyConfig()

  const benefits = useAppSelector(state => state.loyalty.account.data?.benefits)
  const isLoyaltyRegion = useAppSelector(getIsCurrentRegionUserLoyaltyRegion)
  const transferAirports = useAppSelector(state => state.loyalty.config?.transferAirports ?? EmptyArray)
  const flightDetails = useAppSelector(state => state.orders.flightDetails)
  const cruiseSnapshots = useAppSelector(state => state.cruise.cruiseOfferSnapshot)
  const isTasteOfGoldEligibleOrder = useAppSelector(getIsTasteOfGoldEligibleOrder)
  const experienceLists = useAppSelector(state => state.experience.experienceLists)
  const experiences = useAppSelector(state => state.experience.experiences)

  const isInternationalTrip = useMemo(
    () => isOrderInternationalTrip(order, flightDetails, cruiseSnapshots),
    [order, flightDetails, cruiseSnapshots],
  )

  const isOrderEligibleRedeemTransfer = useMemo(
    () => determineOrderLoyaltyRedeemTransferEligibility(
      order,
      benefits,
      isInternationalTrip,
      isLoyaltyRegion,
      transferAirports,
      isTasteOfGoldEligibleOrder,
    ),
    [order, benefits, isInternationalTrip, isLoyaltyRegion, transferAirports, isTasteOfGoldEligibleOrder],
  )

  const transferExperience = useMemo(
    () => determineOrderLoyaltyTransferExperience(order, experienceLists, experiences),
    [order, experienceLists, experiences],
  )

  const geoCoords = useMemo(() => {
    const property = order.items[0]?.offer?.property
    if (!property) return undefined
    const { latitude, longitude } = property
    if (!latitude || !longitude) return undefined
    return { latitude, longitude }
  }, [order])

  const packageIds = useMemo(() => {
    return order.items
      .map(item => item.package?.id)
      .filter((id): id is string => !!id)
  }, [order])

  useEffect(() => {
    if (!isOrderEligibleRedeemTransfer || !geoCoords) return

    dispatch(fetchExperienceList({
      currencyCode: order.currencyCode,
      latitude: geoCoords.latitude,
      longitude: geoCoords.longitude,
      distanceRange: ['0KM', '50KM'],
      categoryCodes: [EXP_CATEGORY_AIRPORT_TRANSFER_ID, EXP_CATEGORY_AIRPORT_LOUNGE_ID],
    }))
  }, [dispatch, isOrderEligibleRedeemTransfer, geoCoords, order.currencyCode])

  useEffect(() => {
    if (!isOrderEligibleRedeemTransfer || !packageIds.length) return

    packageIds.forEach(packageId => {
      dispatch(fetchExperienceList(getComplimentaryTransferParams(packageId)))
    })
  }, [dispatch, isOrderEligibleRedeemTransfer, packageIds])

  useEffect(() => {
    if (transferExperience) {
      dispatch(fetchTransferOptions(transferExperience.id))
    }
  }, [dispatch, transferExperience])
}

export default useLoyaltyOrderViewTransferData
